import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { BRAND_NAME, getCanonicalUrl } from "@/lib/site";

export const DEFAULT_META_DESCRIPTION =
  "Legacy Life & Retirement provides education-first guidance on Infinite Banking, cash flow, retirement planning, long-term care, annuities, and family legacy.";

const upsertMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value) element.setAttribute(key, value);
  });
};

const upsertLink = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value) element.setAttribute(key, value);
  });
};

const removeElement = (selector) => {
  const element = document.head.querySelector(selector);
  if (element) element.remove();
};

export const SEO = ({
  title,
  description,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogUrl,
  ogImage,
  twitterCard = "summary_large_image",
  twitterTitle,
  twitterDescription,
  twitterImage,
  noindex = false,
  noCanonical = false,
  robots,
}) => {
  const { pathname } = useLocation();

  useEffect(() => {
    const pageTitle = title || BRAND_NAME;
    const metaDescription = description || DEFAULT_META_DESCRIPTION;
    const canonical = canonicalUrl || getCanonicalUrl(pathname);
    const resolvedOgTitle = ogTitle || pageTitle;
    const resolvedOgDescription = ogDescription || metaDescription;
    const resolvedOgUrl = ogUrl || canonical;
    const resolvedTwitterTitle = twitterTitle || resolvedOgTitle;
    const resolvedTwitterDescription = twitterDescription || resolvedOgDescription;
    const resolvedTwitterImage = twitterImage || ogImage;
    const robotsContent = robots || (noindex ? "noindex, nofollow" : null);

    document.title = pageTitle;

    upsertMeta('meta[name="description"]', { name: "description", content: metaDescription });

    // Soft-404 mode: a nonexistent page must not advertise itself as a canonical
    // or structured resource. Strip canonical + og:url (and restore them on legit pages).
    if (noCanonical) {
      removeElement('link[rel="canonical"]');
      removeElement('meta[property="og:url"]');
    } else {
      upsertLink('link[rel="canonical"]', { rel: "canonical", href: canonical });
      upsertMeta('meta[property="og:url"]', { property: "og:url", content: resolvedOgUrl });
    }

    upsertMeta('meta[property="og:title"]', { property: "og:title", content: resolvedOgTitle });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: resolvedOgDescription });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    if (ogImage) upsertMeta('meta[property="og:image"]', { property: "og:image", content: ogImage });
    else removeElement('meta[property="og:image"]');

    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: twitterCard });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: resolvedTwitterTitle });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: resolvedTwitterDescription });
    if (resolvedTwitterImage) upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: resolvedTwitterImage });
    else removeElement('meta[name="twitter:image"]');

    if (robotsContent) upsertMeta('meta[name="robots"]', { name: "robots", content: robotsContent });
    else removeElement('meta[name="robots"]');
  }, [
    title,
    description,
    canonicalUrl,
    ogTitle,
    ogDescription,
    ogUrl,
    ogImage,
    twitterCard,
    twitterTitle,
    twitterDescription,
    twitterImage,
    noindex,
    noCanonical,
    robots,
    pathname,
  ]);

  return null;
};
